//#region Imports and Global Definitions
import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import {
    StyleSheet, Text, View, SafeAreaView, ScrollView, Platform, useWindowDimensions, Pressable, Image, ActivityIndicator,
} from 'react-native';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../../styles/commonStyles';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../../components/CustomButton';
import { useAccountContext } from '../../../../hooks/useAccountContext';
import { DEFAULTPROFILE } from '../../../../img/DefaultProfile';
import { useHRAdminEmployees } from '../../../../hooks/useHRAdminEmployees';
import CustomExpandableView from '../../../../components/CustomExpandableView/CustomExpandableView';
import CustomControlledInput from '../../../../components/ControlledComponents/CustomControlledInput';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { z } from "zod";
import { useTranslation } from 'react-i18next';
import Modal from "react-native-modal";
import CustomCalendar from '../../../../components/CustomCalendar/CustomCalendar';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import * as ImagePicker from 'expo-image-picker';
import HRAdminFamilyDetails from '../HRAdminFamilyDetails';
import HRAdminEmplDepts from '../HRAdminEmplDepts';
import HRAdminSalaryEligibility from '../HRAdminSalaryEligibility';
import HRAdminReconciliationBalances from '../HRAdminReconciliationBalances';
import CustomControlledCheckbox from '../../../../components/ControlledComponents/CustomControlledCheckbox';
import CustomControlledDropdown from '../../../../components/ControlledComponents/CustomControlledDropdown';
import moment from 'moment';
import CustomControlledMultiDropdown from '../../../../components/ControlledComponents/CustomControlledMultiDropdown';
import HRAdminSchedule from '../HRAdminSchedule';
import { showMessage } from 'react-native-flash-message';
import HRAdminProvidentFund from '../HRAdminProvidentFund';
import HRAdminMedicalScheme from '../HRAdminMedicalScheme';
import { useSettingsContext } from '../../../../hooks/useSettingsContext';
import { HRAdminCompanyContext } from '../../../../context/HRAdminCompanyContext';
import Swal from 'sweetalert2';
import HRAdminEmployeeTimeSchemes from './HRAdminEmployeeTimeSchemes';
import { DAYNAMES } from '../../../../constants/FormatDate';
//#endregion

// Define Zod schema for form validation
const schema = z.object({
    name: z
        .string()
        .min(2, { message: "Name must be at least 2 characters long" }),
    age: z.number().min(18, { message: "You must be at least 18 years old" }),
});

function WeekGrid({ timescheme }) {

    const { t } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = Platform.OS === 'web';
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height, isPlatformWeb);

    return (
        <View style={[styles.grid, !timescheme && { opacity: 0.5 }]}>
            {/* Row 1: day names */}
            {DAYNAMES.map((d) => (
                <View key={`day-${d}`} style={[styles.cell]}>
                    <Text numberOfLines={1} adjustsFontSizeToFit style={styles.dayCell} >
                        {t(`common:${d}`).substring(0, 3)}
                    </Text>
                </View>
            ))}

            {/* Row 2: values */}
            {DAYNAMES.map((d) => (
                <View key={`time-${d}`} style={styles.cell} >
                    <Text numberOfLines={1} adjustsFontSizeToFit style={styles.timeCell} >
                        {timescheme?.[d] ?? 0}
                    </Text>
                </View>
            ))}
        </View>
    );
}

//#region Helpers
const calculateField = (field, getValues, setValue, getLimitedData, selectedEmployee) => {
    switch (field) {
        case 'age':
            calculateAge(getValues, setValue);
            break;
        case 'annualGrossSalaryGroup':
            calculateGrossSalaryGroup(getValues, setValue, getLimitedData, selectedEmployee);
            break;
        case 'yearsOfServGroup':
            getYearsOfServiceGroup(getValues, setValue);
            break;
        case 'spouseEmplID':
            getSpouseDetails(getValues, setValue, getLimitedData);
            break;
        default:
            break;
    }
}

const calculateGrossSalaryGroup = async (getValues, setValue, getLimitedData, selectedEmployee) => {
    try {
        let annualGrossSalary = 0;
        const res = await getLimitedData(selectedEmployee?.employeeID, "Salary");
        annualGrossSalary = res?.data[0]?.annualGrossSalary || 0;
        let group = determineSalaryGroup(annualGrossSalary);
        setValue('annualGrossSalaryGroup', group);
    } catch (error) {
        setValue('annualGrossSalaryGroup', "<19000");
    }
};

const determineSalaryGroup = (annualGrossSalary) => {
    if (annualGrossSalary <= 19999) {
        return "<19000";
    } else if (annualGrossSalary >= 20000 && annualGrossSalary <= 29999) {
        return "20000-29999";
    } else if (annualGrossSalary >= 30000 && annualGrossSalary <= 39999) {
        return "30000-39999";
    } else if (annualGrossSalary >= 40000 && annualGrossSalary <= 49999) {
        return "40000-49999";
    } else if (annualGrossSalary >= 50000 && annualGrossSalary <= 59999) {
        return "50000-59999";
    } else {
        return ">60000";
    }
};
const getYearsOfServiceGroup = (getValues, setValue) => {
    let years = 0;
    let months = 0;
    try {
        let startDate = getValues('startDate');
        let endDate = getValues('endDate');
        if (startDate && moment(startDate).isValid()) {
            if (endDate && moment(endDate).isValid()) {
                if (moment(endDate).isAfter(moment())) {
                    years = moment().diff(moment(startDate), 'years');
                    months = moment().diff(moment(startDate), 'months');
                } else {
                    years = moment(endDate).diff(moment(startDate), 'years');
                    months = moment(endDate).diff(moment(startDate), 'months');
                }
            } else {
                years = moment().diff(moment(startDate), 'years');
                months = moment().diff(moment(startDate), 'months');
            }
        }

        months = months - (years * 12);
        if (months < 0) {
            months = 12 + months;
            years = years - 1;
        }


        let res;
        if (years >= 0 && years <= 5) {
            res = "0-5";
        } else if (years >= 6 && years <= 10) {
            res = "6-10";
        } else if (years >= 11 && years <= 20) {
            res = "11-20";
        } else if (years >= 21 && years <= 30) {
            res = "21-30";
        } else {
            res = "31+";
        }
        setValue('yearsOfServGroup', res);
    } catch (error) { console.log(error) }
};

const getSpouseDetails = (getValues, setValue, getLimitedData) => {
    let spouseid = getValues("spouseName");
    if (!spouseid) return;
    getLimitedData(spouseid, "mnuEmployee").then((res) => {
        let spouse = res?.data[0];
        setValue("spouseEmplID", spouse?.employeeid);
        setValue("spouseCompany", spouse?.company);
        setValue("spouseServLine", spouse?.serviceLine);
        setValue("spouseDept", spouse?.department);
        setValue("spouseBranch", spouse?.branch);
        setValue("spouseLocation", spouse?.location);
    })
}

const calculateAge = (getValues, setValue) => {
    try {
        const dpDobValue = new Date(getValues("dateOfBirth"));
        const currentDate = new Date();
        const birthdate = dpDobValue;
        let age;
        let month = birthdate.getMonth();
        let day = birthdate.getDay();

        if (month < currentDate.getMonth()) {
            age = currentDate.getFullYear() - birthdate.getFullYear();
        } else if (month === currentDate.getMonth()) {
            if (day < currentDate.getDay()) {
                age = currentDate.getFullYear() - birthdate.getFullYear();
            } else if (day === currentDate.getDay()) {
                age = currentDate.getFullYear() - birthdate.getFullYear();
            } else if (day > currentDate.getDay()) {
                age = currentDate.getFullYear() - birthdate.getFullYear() - 1;
            } else {
                age = 0;
            }
        } else if (month > currentDate.getMonth()) {
            age = currentDate.getFullYear() - birthdate.getFullYear() - 1;
        } else {
            age = 0;
        }
        let m_sAgeGroup;
        if (age >= 18 && age <= 25) {
            m_sAgeGroup = "18-25";
        } else if (age >= 26 && age <= 35) {
            m_sAgeGroup = "26-35";
        } else if (age >= 36 && age <= 45) {
            m_sAgeGroup = "36-45";
        } else if (age >= 46 && age <= 55) {
            m_sAgeGroup = "46-55";
        } else {
            m_sAgeGroup = ">=56";
        }
        setValue('ageGroup', m_sAgeGroup)
        setValue('age', age)
    } catch (error) {
        console.log(error)
    }
}

const FieldComponent = React.memo(function FieldComponent({
    fieldType,
    getValues,
    field,
    handleEditPress,
    control,
    setTempRange,
    setValue,
    styles,
    t,
    showPopUp,
    getLimitedData,
    selectedEmployee,
    salaryEligibility,
    watch,
    index = "",
    getDropdownValues,
    renameKeysInOptions,
    isEdit,
    isEditingMultitimeshemes,
    setIsEditingMultitimeshemes,
    allowMultipleTimeschemes,
    useEmployeeNoAsUsername,
    isEmployeeIDPascal,
    getEmployeeIdBasedOnLocation,
    colors,
}) {


    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedData, setSelectedData] = useState([]);
    const [spouse, setSpouse] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [indexedTimeschemes, setIndexedTimeschemes] = useState(new Map());
    const buttonsWithInput = ['salaryEligibility', 'department'];
    const hasPassport = useWatch({ control, name: 'passportNo' });
    const company = useWatch({ control, name: 'company' });
    const branch = useWatch({ control, name: 'branch' });
    const location = useWatch({ control, name: 'location' });
    const department = useWatch({ control, name: 'department' });
    const hcode = useWatch({ control, name: 'hcode' });
    const paymentLevel = useWatch({ control, name: 'paymentLevel' });
    const L4 = useWatch({ control, name: 'L4' });
    const L5 = useWatch({ control, name: 'L5' });
    const startDate = useWatch({ control, name: 'startDate' })
    const startDateOnLoad = useState(getValues("startDate"))
    const endDate = useWatch({ control, name: 'endDate' })
    const endDateOnLoad = useState(getValues("endDate"))
    const watchField = useWatch({ control, name: field[0] });
    const dateOfBirth = useWatch({ control, name: 'dateOfBirth' })
    const spouseName = useWatch({ control, name: 'spouseName' })
    const opmNameNickname = useWatch({ control, name: 'opmNameNickname' })
    //#region UseEffects
    useEffect(() => {
        if (fieldType !== 'autocalculated' && field[0] !== 'ageGroup') return;
        calculateField(field[0], getValues, setValue, getLimitedData, selectedEmployee);
    }, [dateOfBirth]);

    useEffect(() => {
        if (field[0] !== 'annualGrossSalaryGroup') return;
        calculateField(field[0], getValues, setValue, getLimitedData, selectedEmployee);
    }, []);

    useEffect(() => {
        if (fieldType !== 'autocalculated') return;
        calculateField(field[0], getValues, setValue, getLimitedData, selectedEmployee);
    }, [])

    useEffect(() => {
        if (fieldType !== 'autocalculated') return;
        calculateField(field[0], getValues, setValue, getLimitedData, selectedEmployee);
    }, [spouseName])

    useEffect(() => {
        let spouse = getValues("spouseName");
        if (spouse) {
            setValue("spouseWorkingForDeloitte", true);
            setSpouse(true);
        }
        else {
            setValue("spouseWorkingForDeloitte", false);
            setSpouse(false);
        }
    }, [])

    useEffect(() => {
        if (field[0] === 'salaryEligibility') {
            let eligible = salaryEligibility[0]?.exceptionThirteenthSalary;
            if (eligible)
                setValue('salaryEligibility', 'Eligible');
            else setValue('salaryEligibility', 'Not Eligible');
        }
    }, [salaryEligibility])

    useEffect(() => {
        const spouse = getValues("spouseWorkingForDeloitte");
        if (field[0] === 'spouseName') {
            if (!spouse) {
                setValue("spouseName", "");
                setValue("spouseEmplID", "");
                setValue("spouseCompany", "");
                setValue("spouseServLine", "");
                setValue("spouseDept", "");
                setValue("spouseBranch", "");
                setValue("spouseLocation", "");
            }
            field[1].isDisabled = !spouse;
        }
    }, [spouse, getValues("spouseWorkingForDeloitte")])

    useEffect(() => {
        const nickname = getValues('opmNameNickname')
        setValue('opmNameNickname', nickname)
    }, [opmNameNickname])

    useEffect(() => {
        if (fieldType !== 'autocalculated') return;
        calculateField(field[0], getValues, setValue, getLimitedData, selectedEmployee);
    }, [field, getValues, setValue]);

    useEffect(() => {
        if (field[1]?.type !== 'date') return;
        let fieldDate = getValues(field[0]);
        if (fieldDate !== '' && fieldDate !== null) {
            setTempRange(fieldDate)
            setSelectedData({ startDate: fieldDate })
        }
        else setSelectedData([]);
    }, [])

    useEffect(() => {
        if (Object.keys(selectedData).length > 0) {
            setValue(field[0], selectedData.startDate);
        }
    }, [selectedData])

    useEffect(() => {
        let hasMedicalScheme = getValues("memberOfMedicalScheme");
        if (field[0] === 'medScheme') {
            if (hasMedicalScheme)
                field[1].isDisabled = false;
            else field[1].isDisabled = true;
        }
    }, [])

    useEffect(() => {
        let hasProvidentFund = getValues("memberOfProvFund");
        if (field[0] === 'memberOfProvFund') {
            if (hasProvidentFund)
                field[1].isDisabled = false;
            else field[1].isDisabled = true;
        }
    }, [])

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'branch') return;
        changeDropdownValues('branch');
    }, [company]);

    useEffect(() => {
        if (field[0] !== 'location') return;
        changeDropdownValues('location');
    }, [company, branch]);

    useEffect(() => {
        if (field[0] !== 'buildingFloor') return;
        changeDropdownValues('buildingFloor');
    }, [company, branch, location]);

    //* This updates employeid on new id for pascal only 
    //* This SHOULD ONLY work on new employees 
    useEffect(() => {
        if (isEdit) return;
        if (location !== undefined && isEmployeeIDPascal) {
            getEmployeeIdBasedOnLocation(company, location, branch).then((resp) => {
                console.log(resp)
                let newId = incrementId(resp);
                setValue('employeeID', newId);
            }).catch((ex) => { })
        }
    }, [location]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'department') return;
        changeDropdownValues('department');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'employmentContext') return;
        changeDropdownValues('employmentContext');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'hcode') return;
        changeDropdownValues('hcode');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'emplType') return;
        changeDropdownValues('emplType');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'paymentLevel') return;
        changeDropdownValues('paymentLevel');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'jobTitle') return;
        changeDropdownValues('jobTitle');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'bank') return;
        changeDropdownValues('bank');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'paymentMethod') return;
        changeDropdownValues('paymentMethod');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'specialDuties') return;
        changeDropdownValues('specialDuties');
    }, [company]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'unionName') return;
        changeDropdownValues('unionName');
    }, [company]);

    useEffect(() => {
        // if (isEdit) return;
        if (field[0] !== 'parkLot') return;
        changeDropdownValues('parkLot');
    }, [company, location, branch]);

    useEffect(() => {
        if (field[0] !== 'timescheme') return;
        if (!company || !branch || !location) return;
        changeDropdownValues('timescheme');
    }, [company, location, branch]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'L4') return;
        changeDropdownValues('L4');
    }, [department]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'L5') return;
        changeDropdownValues('L5');
    }, [L4]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'L6') return;
        changeDropdownValues('L6');
    }, [L5]);

    useEffect(() => {
        if (field[0] !== 'staffLevel') return;
        changeDropdownValues('staffLevel');
    }, [hcode]);

    useEffect(() => {
        if (isEdit) return;
        if (field[0] !== 'paymentSubLevel') return;
        changeDropdownValues('paymentSubLevel');
    }, [paymentLevel]);

    useEffect(() => {
        if (!endDate || !startDate) return;
        if (endDate === (endDateOnLoad ? endDateOnLoad[0].toString() : '')) {
            if (startDate === startDateOnLoad[0].toString())
                return;
        }
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        const currentDateObj = new Date();
        if ((startDateObj <= currentDateObj) && (endDateObj > currentDateObj || !endDate)) {
            setValue('active', true);
        } else
            setValue('active', false);

    }, [startDate, endDate])
    //#endregion

    const onShowCalendarPress = () => {
        setShowCalendar(!showCalendar);
        if (showCalendar) {
            setValue(field[0], selectedData.startDate);
        }
    }

    const incrementId = (id) => {
        if (typeof id !== 'string') {
            throw new Error('ID must be a string');
        }

        const match = id.match(/^([A-Za-z])(\d+)$/);
        if (!match) {
            throw new Error('ID must be: first character a letter, followed by digits (e.g., A0001)');
        }

        const letter = match[1];
        const digits = match[2];
        const width = digits.length;

        const num = Number(digits);
        const incremented = num + 1;
        const incrementedStr = String(incremented);

        const padded =
            incrementedStr.length >= width
                ? incrementedStr
                : incrementedStr.padStart(width, '0');

        return letter + padded;
    };

    function hasSpouseAtDeloitte() {
        let spouseAtDeloitte = getValues("spouseWorkingForDeloitte");
        if (field[0] === 'spouseName') {
            if (spouseAtDeloitte) {
                field[1].isDisabled = false;
                return true;
            }
            else {
                field[1].isDisabled = true;
                return false;
            }
        }
    }

    function userHasPassport() {
        if (field[0] === 'doIssuePassport' || field[0] === 'passportExpDate') {
            if (!isValidString(hasPassport)) {
                field[1].isDisabled = true;
            }
            else field[1].isDisabled = false;
        }
    }

    function changeDropdownValues(dropdown) {
        let sParams = [];
        switch (dropdown) {
            case 'branch':
                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { branch: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.branch);
                    setIsLoading(false);
                });

                break;
            case 'location':
                if (!branch || !company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { location: { sFilt1: branch, sFilt2: company, sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.location);
                    setIsLoading(false);
                });
                break;
            case 'buildingFloor':

                if (!location) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { buildingFloor: { sFilt1: branch, sFilt2: company, sFilt3: location } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.buildingFloor);
                    setIsLoading(false);
                });
                break;
            case 'timescheme':
                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { timescheme: { sFilt1: company, sFilt2: branch, sFilt3: location } },
                ]
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    setIndexedTimeschemes(new Map(res?.timescheme?.map((timescheme) => [timescheme.timescheme, timescheme]) ?? []))
                    field[1]['options'] = renameKeysInOptions(res?.timescheme);
                    setIsLoading(false);
                });

                break;
            case 'department':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { department: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.department);
                    setIsLoading(false);
                });
                break;
            case 'employmentContext':
                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { employmentContext: { sFilt1: company, sFilt2: "", sFilt3: "" } }
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.employmentContext);
                    setIsLoading(false);
                });
                break;
            case 'hcode':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { hcode: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.hcode);
                    setIsLoading(false);
                });
                break;
            case 'emplType':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { etype: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.etype);
                    setIsLoading(false);
                });
                break;
            case 'paymentLevel':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { paymentLevel: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.paymentLevel);
                    setIsLoading(false);
                });
                break;
            case 'jobTitle':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { jobTitle: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.jobTitle);
                    setIsLoading(false);
                });
                break;
            case 'bank':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { bank: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.bank);
                    setIsLoading(false);
                });
                break;
            case 'paymentMethod':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { paymentMethod: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.paymentMethod);
                    setIsLoading(false);
                });
                break;
            case 'specialDuties':
                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { specialDuties: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.specialDuties);
                    setIsLoading(false);
                });
                break;
            case 'unionName':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { unionName: { sFilt1: company, sFilt2: "", sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    field[1]['options'] = renameKeysInOptions(res?.unionName);
                    setIsLoading(false);
                });
                break;
            case 'parkLot':

                if (!company) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { parkLot: { sFilt1: location, sFilt2: branch, sFilt3: company } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.parkLot);
                    }
                    setIsLoading(false);
                });
                break;
            case 'L4':

                if (!company || !department) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { L4: { sFilt1: company, sFilt2: department, sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.L4);
                    }
                    setIsLoading(false);
                });
                break;
            case 'L5':

                if (!L4) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { L5: { sFilt1: company, sFilt2: department, sFilt3: L4 } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.L5);
                    }
                    setIsLoading(false);
                });
                break;
            case 'L6':

                if (!L5) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { L6: { sFilt1: company, sFilt2: department, sFilt3: L4, sFilt4: L5 } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.L6);
                    }
                    setIsLoading(false);
                });
                break;
            case 'staffLevel':
                if (!hcode) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { staffLevel: { sFilt1: company, sFilt2: hcode, sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.staffLevel);
                    }
                    setIsLoading(false);
                });
                break;
            case 'paymentSubLevel':

                if (!L5) {
                    field[1]['options'] = [];
                    return;
                }
                sParams = [
                    { paymentSubLevel: { sFilt1: company, sFilt2: paymentLevel, sFilt3: "" } },
                ];
                setIsLoading(true);
                getDropdownValues(sParams).then((res) => {
                    if (Array.isArray(res) && res.length === 0) {
                        field[1]['options'] = [];
                    } else {
                        field[1]['options'] = renameKeysInOptions(res?.paymentSubLevel);
                    }
                    setIsLoading(false);
                });
                break;
            default:
                break;
        }
    }

    const createUsername = () => {
        const fullName = getValues('name');
        const firstName = getValues('firstName');
        const lastName = getValues('lastName');
        if (!fullName) return;
        if (!isEdit && !useEmployeeNoAsUsername) {
            if ((lastName && lastName.trim() === '' && lastName.trim().length > 0) || !lastName)
                setValue('username', constructUsername(fullName));
            else if (lastName && lastName.trim() !== '' && lastName.trim().length > 0)
                setValue('username', constructUsername(firstName + ' ' + lastName));
        }
        else if (!isEdit && useEmployeeNoAsUsername) {
            setValue('username', watch('employeeNumber'));
        }
    }

    const constructUsername = (fullName) => {
        let sUName = '';
        try {
            const str = fullName.split(' ');
            const i = str.length; // number of substrings
            // Construct username
            for (let j = 0; j < i; j++) {
                if (j < i - 1) {
                    sUName += str[j].charAt(0);
                } else {
                    sUName += str[j];
                }
            }
            return sUName.toLowerCase();
        } catch (error) {
            return fullName;
        }
    }

    const stringIsEmpty = (str) => {
        return !(str.trim() === '' && str.trim().length > 0);
    }

    const isValidString = (str) => {
        return typeof str === 'string' && str.trim().length > 0;
    };

    const handleFocus = () => {
        // if (field[0] === 'username')
        //     createUsername();
    }

    hasSpouseAtDeloitte();
    userHasPassport();

    switch (fieldType) {
        case 'dropdown':
            if (allowMultipleTimeschemes && field[0] === 'timescheme') return (<></>)
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>
                    <View style={[styles.middleColumn]}>
                        {!isLoading ? (
                            field[1]?.allowsMultiple ? (
                                <CustomControlledMultiDropdown
                                    control={control}
                                    name={field[0]}
                                    type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                        || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                    options={field[1]?.options}
                                    setValue={setValue}
                                    disabled={field[1]?.isDisabled}
                                    direction="TOP"
                                    rules={field[1]}
                                />
                            ) : (
                                <>
                                    <CustomControlledDropdown
                                        multipleSelect={false}
                                        control={control}
                                        name={field[0]}
                                        type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                            || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'EDIT_EMPLOYEE'}
                                        options={field[1]?.options}
                                        setValue={setValue}
                                        disabled={field[1]?.isDisabled}
                                        rules={field[1]}
                                    />
                                    {
                                        // TODO: REFACTOR THIS WHOLE THING....
                                        // This shouldnt go , 
                                        // form field type (dropdown) -> component
                                        // but
                                        // form field (field[0]) -> component
                                        // ex. field[0] === timescheme => <Timeschemes/>
                                        //
                                        // Even better? No dynamic forms, just write the form in the JSX...
                                        (!allowMultipleTimeschemes && field[0] === 'timescheme') &&
                                        // TODO:
                                        // Break this in to component
                                        (
                                            <WeekGrid timescheme={indexedTimeschemes.get(getValues('timescheme'))} />
                                        )
                                    }


                                </>
                            )
                        ) : (
                            <CustomLabel
                                value={t('common:loading')}
                                type='EDIT_EMPLOYEE_LOADING'
                            />
                        )}
                    </View>
                    {(allowMultipleTimeschemes && field[0] === "branch") ?
                        <View style={[styles.rightColumn, { flexDirection: 'row' }]}>
                            {/* <Ionicons
                                name="add-outline"
                                style={[styles.icons,!company && {opacity: 0.5 }]}
                                disabled={!company}
                                onPress={() => {
                                    setIsEditingMultitimeshemes(!isEditingMultitimeshemes)
                                }}
                            /> */}
                            <Pressable
                                disabled={!company}
                                onPress={() => setIsEditingMultitimeshemes(!isEditingMultitimeshemes)}
                                style={({ pressed }) => [
                                    styles.iconButton,
                                    !company && styles.disabled,
                                    pressed && company && styles.pressed,
                                ]}
                                hitSlop={10}
                                accessibilityRole="button"
                                accessibilityLabel="Add"
                            >
                                <Ionicons name="add-outline" style={styles.icons} />
                            </Pressable>
                            {field[1]?.required &&
                                <CustomLabel
                                    value="*"
                                    type='error'
                                />
                            }
                        </View>
                        :

                        <View style={styles.rightColumn}></View>
                    }
                </View>
            )
        case 'checkbox':
            return (
                (field[1]?.hasMultiple === true ? (
                    <CustomControlledCheckbox
                        control={control}
                        name={field[1].name[index]}
                        type="EDIT_EMPLOYEE"
                    />
                ) : (
                    <View style={styles.box}>
                        <View style={styles.leftColumn}>
                            <CustomLabel
                                value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                                type='EDIT_EMPLOYEE_HEADER'
                            />
                        </View>
                        <View style={styles.middleColumn}>
                            <CustomControlledCheckbox
                                control={control}
                                name={field[0]}
                                type="EDIT_EMPLOYEE"
                            />
                        </View>
                        <View style={styles.rightColumn}></View>
                    </View>
                ))
            )
        case 'date':
            return (
                <View>
                    <SafeAreaView style={styles.box}>
                        <View style={styles.leftColumn}>
                            <CustomLabel
                                value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                                type='EDIT_EMPLOYEE_HEADER'
                            />
                        </View>
                        <View style={styles.middleColumn}>
                            <CustomControlledInput
                                name={field[0]}
                                control={control}
                                editable={false}
                                rules={field[1]}
                                type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                    || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'MAININPUT'} />
                        </View>
                        <View style={[styles.rightColumn, { flexDirection: 'row' }]}>
                            {!field[1]?.isDisabled &&
                                <Ionicons
                                    name={showCalendar ? "calendar" : "calendar-outline"}
                                    style={styles.icons}
                                    onPress={() => onShowCalendarPress(field[0])}
                                />
                            }
                            {field[1]?.required &&
                                <CustomLabel
                                    value="*"
                                    type='error'
                                />
                            }
                        </View>
                    </SafeAreaView>
                    {showCalendar &&
                        <SafeAreaView>
                            <View style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: 10,
                                borderWidth: 0,
                                borderColor: colors.border_card,
                            }}>
                                <CustomCalendar
                                    calendarType={'RangePicker'}
                                    setRange={setSelectedData}
                                    range={selectedData}
                                    allowSingleDate={true}
                                    startingYear={1950}
                                />
                            </View>
                        </SafeAreaView>
                    }
                </View>
            )
        case 'autocalculated':
            return (
                <View style={styles.box}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>
                    {field[1]?.isDropdown ? <View style={styles.middleColumn}>
                        <CustomControlledDropdown
                            control={control}
                            name={field[0]}
                            type="EDIT_EMPLOYEE"
                            options={field[1]?.options}
                            setValue={setValue}
                            disabled={field[1]?.isDisabled}
                        />
                    </View>
                        :
                        <View style={styles.middleColumn}>
                            <CustomControlledInput
                                name={field[0]}
                                control={control}
                                editable={false}
                                rules={field[1]}
                                type='MAININPUT'
                            />
                        </View>
                    }
                    <View style={styles.rightColumn}></View>
                </View>
            )
        case 'divider':
            return (
                <View style={{ marginTop: '2%' }}>
                    <CustomLabel
                        value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                        type='EDIT_EMPLOYEE_DIVIDER'
                    />
                    <SafeAreaView style={styles.horizontalLine} />
                </View>
            )
        case 'button':
            return (
                <View style={styles.box}>
                    <View style={[styles.leftColumn, { marginRight: 4 }]}>
                        <CustomLabel
                            value={field[1]?.title ? t(`common:${field[1]?.title}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE_HEADER'
                        />
                    </View>
                    <View style={[styles.middleColumn]}>
                        {buttonsWithInput.includes(field[0]) &&
                            <CustomControlledInput
                                name={field[0]}
                                control={control}
                                editable={false}
                                rules={field[1]}
                                type='MAININPUT'
                            />
                        }
                    </View>
                    <View style={styles.rightColumn}>
                        <Ionicons
                            name={field[1]?.icon === undefined ? 'layers-outline' : field[1]?.icon}
                            style={[styles.icons, { fontSize: 30, }]}
                            onPress={() => { showPopUp(field[0]) }}
                            disabled={field[1]?.isDisabled}
                        />
                    </View>
                </View>

            )
        default:
            return (
                field[1]?.hasMultiple ? (
                    <CustomControlledInput
                        name={field[1].name[index]}
                        control={control}
                        rules={field[1]}
                        type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                            || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'MAININPUT'}
                        handleFocus={handleFocus}
                    />
                ) : (
                    <View style={styles.box}>
                        <View style={styles.leftColumn}>
                            {field[0] === 'timeCardNo' && process.env.REACT_APP_CLIENT === 'atlas' ? (
                                <CustomLabel
                                    value={t('common:jdeNum')}
                                    type="EDIT_EMPLOYEE_HEADER"
                                />
                            ) : field[0] === 'telmob2' && process.env.REACT_APP_CLIENT === 'atlas' ? (
                                <CustomLabel
                                    value={t('common:workmob2')}
                                    type="EDIT_EMPLOYEE_HEADER"
                                />
                            ) : (
                                <CustomLabel
                                    value={
                                        field[1]?.title
                                            ? t(`common:${field[1].title}`)
                                            : t(`common:${field[0]}`)
                                    }
                                    type="EDIT_EMPLOYEE_HEADER"
                                />
                            )}
                        </View>
                        <View style={styles.middleColumn}>
                            <CustomControlledInput
                                name={field[0]}
                                control={control}
                                editable={field[1]?.isDisabled ? false : true}
                                rules={field[1]}
                                type={((field[1]?.required && (watchField?.length <= 0 || watchField === undefined))
                                    || field[1]?.error) ? 'EDIT_EMPLOYEE_REQUIRED' : 'MAININPUT'}
                                handleFocus={handleFocus}
                            />
                        </View>
                        <View style={styles.rightColumn}>
                            {((field[1]?.required && (watchField?.length <= 0 || watchField === undefined)) || field[1]?.error) &&
                                <CustomLabel
                                    value="*"
                                    type='error'
                                />
                            }
                        </View>
                    </View>
                )
            )
    }
})


const DisplayField = React.memo(({ styles, field, control, t, handleEditPress, getValues, setTempRange, setValue,
    showPopUp, getLimitedData, selectedEmployee, salaryEligibility, watch, getDropdownValues, renameKeysInOptions, isEdit, isEditingMultitimeshemes, setIsEditingMultitimeshemes, allowMultipleTimeschemes, useEmployeeNoAsUsername, isEmployeeIDPascal, getEmployeeIdBasedOnLocation, colors, dropdowns,
}) => {
    if (field[1]?.isVisible === false) return;
    let types = Array.isArray(field[1]?.type) ? field[1]?.type : [field[1]?.type ? field[1]?.type : ''];
    return (

        field[1]?.isVisible ? <SafeAreaView key={field[0]}>
            {types.length >= 2 ? (
                <View style={styles.row}>
                    <View style={styles.leftColumn}>
                        <CustomLabel
                            value={field[1]?.label ? t(`common:${field[1]?.label}`) : t(`common:${field[0]}`)}
                            type='EDIT_EMPLOYEE'
                        />
                    </View>
                    <View style={[styles.middleColumn, { flexDirection: 'row', alignItems: 'center' }]}>
                        {types.map((fieldType, index) => (
                            <FieldComponent
                                key={`${field[0]}-${fieldType}`}
                                fieldType={fieldType}
                                getValues={getValues}
                                field={field}
                                handleEditPress={handleEditPress}
                                control={control}
                                setTempRange={setTempRange}
                                setValue={setValue}
                                styles={styles}
                                t={t}
                                showPopUp={showPopUp}
                                getLimitedData={getLimitedData}
                                selectedEmployee={selectedEmployee}
                                salaryEligibility={salaryEligibility}
                                watch={watch}
                                index={index}
                                getDropdownValues={getDropdownValues}
                                renameKeysInOptions={renameKeysInOptions}
                                isEdit={isEdit}
                                isEditingMultitimeshemes={isEditingMultitimeshemes}
                                setIsEditingMultitimeshemes={setIsEditingMultitimeshemes}
                                allowMultipleTimeschemes={allowMultipleTimeschemes}
                                useEmployeeNoAsUsername={useEmployeeNoAsUsername}
                                isEmployeeIDPascal={isEmployeeIDPascal}
                                getEmployeeIdBasedOnLocation={getEmployeeIdBasedOnLocation}
                                colors={colors} />
                        ))}
                    </View>
                    <View style={styles.rightColumn}></View>
                </View>
            ) : (
                types.map((fieldType, index) => (

                    <FieldComponent
                        key={`${field[0]}-${fieldType}-${index}`}
                        fieldType={fieldType}
                        getValues={getValues}
                        field={field}
                        handleEditPress={handleEditPress}
                        control={control}
                        setTempRange={setTempRange}
                        setValue={setValue}
                        styles={styles}
                        t={t}
                        showPopUp={showPopUp}
                        getLimitedData={getLimitedData}
                        selectedEmployee={selectedEmployee}
                        salaryEligibility={salaryEligibility}
                        watch={watch}
                        index={index}
                        getDropdownValues={getDropdownValues}
                        renameKeysInOptions={renameKeysInOptions}
                        isEdit={isEdit}
                        isEditingMultitimeshemes={isEditingMultitimeshemes}
                        setIsEditingMultitimeshemes={setIsEditingMultitimeshemes}
                        allowMultipleTimeschemes={allowMultipleTimeschemes}
                        useEmployeeNoAsUsername={useEmployeeNoAsUsername}
                        isEmployeeIDPascal={isEmployeeIDPascal}
                        getEmployeeIdBasedOnLocation={getEmployeeIdBasedOnLocation}
                        colors={colors} 
                        dropdowns={dropdowns}/>
                ))
            )}
        </SafeAreaView> : <></>

    );
});

const MemoizedCustomButton = React.memo(({ onPress, t, text, setExpanded, isExpanded, styles }) => {
    return (
        <CustomButton
            text={t(`common:${text}`)}
            onPress={() => onPress(setExpanded)}
            type="EDIT_EMPLOYEE_EXPANDABLE"
            iconName={isExpanded ? "chevron-down-outline" : "chevron-forward-outline"}
            show="EDIT_EMPLOYEE_EXPANDABLE"
            textTop="0"
            sz={25}
        />
    );
});

const Render = React.memo(({ render, arr, styles }) => {
    // console.log(arr)
    const visibleItems = arr.filter(item => item[1].isVisible === true);
    return (
        <SafeAreaView style={[styles.row, { height: '100%', }]}>
            {/* left column */}
            <SafeAreaView style={[styles.column]}>
                {render(visibleItems.slice(0, Math.ceil(visibleItems.length / 2 - 1)))}
            </SafeAreaView>

            {/* middle column */}
            <SafeAreaView style={styles.verticalLine} />

            {/* right column */}
            <SafeAreaView style={styles.column}>
                {render(visibleItems.slice(Math.ceil(visibleItems.length / 2 - 1)))}
            </SafeAreaView>
        </SafeAreaView>
    )
});


//#endregion

const HRAdminEditEmployeeScreen = (props) => {

    //#region Constants
    const { account } = useAccountContext();
    const [originalUserData, setOriginalUserData] = useState(null);
    const [personalTabArray, setPersonalTabArray] = useState([]);
    const [isEditingMultitimeshemes, setIsEditingMultitimeshemes] = useState(false)
    const [employmentTabArray, setEmploymentTabArray] = useState([]);
    const [miscellaneousTabArray, setMiscellaneousTabArray] = useState([]);
    const [additionalInfoArray, setAdditionalInfoArray] = useState([]);
    const [tempRange, setTempRange] = useState({}); // strings of format yyyy-MM-dd
    const [familyDetails, setFamilyDetails] = useState([]);
    const ref = useRef(null);
    const [emplDepts, setEmplDepts] = useState([]);
    const [salaryEligibility, setSalaryEligibility] = useState([]);
    const [dropdowns, setDropdowns] = useState({});
    const { t } = useTranslation();
    const [familyDropdowns, setFamilyDropdowns] = useState({
        relType: { sFilt1: "", sFilt2: "", sFilt3: "" },
        sex: { sFilt1: "", sFilt2: "", sFilt3: "" },
        occupation: { sFilt1: "", sFilt2: "", sFilt3: "" },
        underSponsorship: { sFilt1: "", sFilt2: "", sFilt3: "" },
    })
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = Platform.OS === 'web';
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { getEmployeeData, getMandatoryFields, postUserData, getDropdownValues,
        CalculateEmplRevisedAllowance, reconcileBalances,
        getLimitedData, getTimescheme, saveEmplSpecialDuties, ValidateUniqueConstraints,
        createLeaveAnalysis, getEmployeeIdBasedOnLocation
    } = useHRAdminEmployees();
    const [isLoading, setIsLoading] = useState(true);
    const [isPersonalExpanded, setIsPersonalExpanded] = useState(false);
    const [isEmploymentExpanded, setIsEmploymentExpanded] = useState(false);
    const [isMiscellaneousExpanded, setIsMiscellaneousExpanded] = useState(false);
    const [isAdditionalExpanded, setIsAdditionalExpanded] = useState(false);
    const [reconciliationBalances, setReconciliationBalances] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const prevModalVisible = useRef();
    const [modalData, setModalData] = useState({});
    const { allowMultipleTimeschemes, employmentTabData, miscellaneousTabData, additionalTabData, personalTabData, isEmployeeIDPascal } = useSettingsContext();
    const { selectedCompany, selectedEmployee, setSelectedEmployee } = useContext(HRAdminCompanyContext);

    const { useEmployeeNoAsUsername } = false;
    const [emplCompany, setEmplCompany] = useState('');
    const {
        isEdit
    } = props;
    //#region Use Effects
    useEffect(() => {
        if (modalData?.title !== 'familyDetailsTitle') return;
        setModalData({ ...modalData, ['dataArr']: familyDetails })
    }, [familyDetails])

    useEffect(() => {
        if (modalData?.title !== 'emplDeptsTitle') return;
        setModalData({ ...modalData, ['dataArr']: emplDepts })
    }, [emplDepts])

    useEffect(() => {
        if (modalData?.title !== 'salaryEligibilityTitle') return;
        setModalData({ ...modalData, ['dataArr']: salaryEligibility })
    }, [salaryEligibility])

    useEffect(() => {
        if (modalData?.title !== 'reconciliationBalancesTitle') return;
        setModalData({ ...modalData, ['dataArr']: reconciliationBalances })
    }, [reconciliationBalances])

    // useEffect(() => {
    //     if (modalData?.title !== 'salaryEligibilityTitle') return;
    //     setModalData({ ...modalData, ['dataArr']: salaryEligibility })
    // }, [])

    useEffect(() => {
        renderModal(modalData)
    }, [modalData])

    useEffect(() => {
        /* If the user is a new employee, then there are no data to fetch from the database as the user hasn't been inserted yet. */
        if (!isEdit) {
            const sParams = [
                { title: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { sex: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { town: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { country: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { company: { sFilt1: "", sFilt2: account.employeeId, sFilt3: "" } },
                { timetable: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { spouseName: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                { ...familyDropdowns }
            ]
            setIsLoading(true);
            getDropdowns(sParams).then((dropdownValues) => {
                setDropdowns(dropdownValues)

                fillExtraDropdowns(familyDropdowns, 'sex', dropdownValues, 'sex')
                familyDropdowns['underSponsorship'].options = dropdownValues['underSponsorship'].map(item => ({
                    value: item.code === 'True',
                    label: item.description
                }));
                fillExtraDropdowns(additionalTabData, 'companySecondary', dropdownValues, 'company')
                fillExtraDropdowns(additionalTabData, 'locationSecondary', dropdownValues, 'location')
                fillExtraDropdowns(personalTabData, 'homeCity', dropdownValues, 'town')
                fillExtraDropdowns(personalTabData, 'homeCountry', dropdownValues, 'country')

                fillExtraDropdowns(personalTabData, 'scndNationality', dropdownValues, 'country')
                fillExtraDropdowns(personalTabData, 'nationality', dropdownValues, 'country')

                fetchMandatoryFields().then(() => {
                    setPersonalTabArray(Object.entries(personalTabData));
                    setEmploymentTabArray(Object.entries(employmentTabData))
                    setMiscellaneousTabArray(Object.entries(miscellaneousTabData))
                    setAdditionalInfoArray(Object.entries(additionalTabData))

                    setIsLoading(false);
                })
            })
            return;
        }
        // * Load Selected Employee 
        loadEmployeeData()
    }, [selectedEmployee])

    // * Moving enabling / disabling fields on edit because if it was in the on load use effect (runs only once []) and you move from selected employee
    // * to new employee that use effect is not run as a result the drop downs remain disabled. By moving them to a useEffect with isEdit every time that variable 
    // * is set we recheck the enable / disable
    useEffect(() => {
        // * Employee Tabs are read from the Database. Some of the Required Fields have other Enable Disable States in Create and Edit employee
        // * As a result override what ever is read from the databse. 
        employmentTabData['company'].isDisabled = isEdit;
        employmentTabData['branch'].isDisabled = allowMultipleTimeschemes;
        employmentTabData['location'].isDisabled = allowMultipleTimeschemes;
        employmentTabData['buildingFloor'].isDisabled = allowMultipleTimeschemes;
        employmentTabData['department'].isDisabled = isEdit;
        employmentTabData['L4'].isDisabled = isEdit;
        employmentTabData['L5'].isDisabled = isEdit;
        employmentTabData['L6'].isDisabled = isEdit;
        employmentTabData['jobTitle'].isDisabled = isEdit;
        employmentTabData['hcode'].isDisabled = isEdit;
        employmentTabData['employmentContext'].isDisabled = isEdit;
        employmentTabData['timescheme'].isDisabled = allowMultipleTimeschemes;
        personalTabData['employeeID'].isDisabled = isEdit;
    }, [isEdit])

    useEffect(() => {
        if (prevModalVisible.current === true && modalVisible === false) {
            onClose()
            loadEmployeeData()
        }
        prevModalVisible.current = modalVisible;
    }, [modalVisible])
    //#endregion

    //#region Helpers

    const loadEmployeeData = () => {
        getEmployeeData(selectedEmployee?.employeeID).then((res) => {
            const userData = res[0];

            setEmplCompany(userData['company'])
            setOriginalUserData(userData);
            setSalaryEligibility([{
                exceptionThirteenthSalary: userData["exceptionThirteenthSalary"],
                dateOfThirteenthSalaryException: userData["dateOfThirteenthSalaryException"]
            }])
            fetchMandatoryFields().then(() => {

                const sParams = [
                    { title: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { sex: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { town: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { country: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { company: { sFilt1: selectedCompany?.code, sFilt2: account.employeeId, sFilt3: "" } },
                    { branch: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { department: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { departmentServLineFunc: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { location: { sFilt1: userData["branch"], sFilt2: selectedCompany?.code, sFilt3: "" } },
                    { employmentContext: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { hcode: { sFilt1: userData['company'], sFilt2: "", sFilt3: "" } },
                    { timetable: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { emplType: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { paymentLevel: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { paymentSubLevel: { sFilt1: selectedCompany?.code, sFilt2: userData["paymentLevel"], sFilt3: "" } },
                    { jobTitle: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { bank: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { paymentMethod: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { specialDuties: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { L4: { sFilt1: selectedCompany?.code, sFilt2: userData["department"], sFilt3: "" } },
                    { L5: { sFilt1: selectedCompany?.code, sFilt2: userData["department"], sFilt3: userData["L4"] } },
                    { L6: { sFilt1: selectedCompany?.code, sFilt2: userData["department"], sFilt3: userData["L4"], sFilt4: userData["L5"] } },
                    { unionName: { sFilt1: selectedCompany?.code, sFilt2: "", sFilt3: "" } },
                    { parkLot: { sFilt1: userData["location"], sFilt2: userData["branch"], sFilt3: selectedCompany?.code } },
                    { buildingFloor: { sFilt1: userData["branch"], sFilt2: selectedCompany?.code, sFilt3: userData["location"] } },
                    { spouseName: { sFilt1: "", sFilt2: "", sFilt3: "" } },
                    { staffLevel: { sFilt1: selectedCompany?.code, sFilt2: userData["hcode"], sFilt3: "" } },
                    { ...familyDropdowns }
                ]

                if (!allowMultipleTimeschemes) {
                    // Field is dissabled on multiple timeschemes
                    sParams.push({ timescheme: { sFilt1: selectedCompany?.code, sFilt2: userData["branch"], sFilt3: userData["location"] } })
                }

                let dropdownValues = {};
                let family = { ...familyDropdowns };
                getDropdownValues(sParams).then((res) => {
                    if (Object.keys(res).length > 0) {
                        for (const key in res) {
                            let result;
                            let tab;
                            const renamedField = renameKeysInOptions(res[key]);
                            // * departmentServLineFunc only for EMPLDEPTS drop down so ignore when updating employee_tabs dropdowns 
                            if (key !== 'departmentServLineFunc') {
                                if (key in personalTabData) {
                                    personalTabData[key].options = renamedField;
                                    tab = personalTabData;
                                }
                                else if (key in employmentTabData) {
                                    employmentTabData[key].options = renamedField;
                                    tab = employmentTabData;
                                }
                                else if (key in miscellaneousTabData) {
                                    miscellaneousTabData[key].options = renamedField;
                                    tab = miscellaneousTabData;
                                }
                                else if (key in additionalTabData) {
                                    additionalTabData[key].options = renamedField;
                                    tab = additionalTabData;
                                }
                                if (key in family && !tab) {
                                    family[key].options = renamedField;
                                    tab = family;
                                }
                                result = findObjectByValue(tab[key]?.options, userData[key]);

                                if (tab[key]?.type !== 'dropdown' && tab !== family) {
                                    if (result) {
                                        tab[key].description = result;
                                        tab[key].value = userData[key];
                                        userData[key] = result;
                                    }
                                    else {
                                        tab[key].label = userData[key];
                                        tab[key].value = userData[key];
                                    }
                                }
                            }
                            dropdownValues[key] = renamedField
                        }

                        //#region Fill Left-Out Dropdowns
                        family['sex'].options = renameKeysInOptions(res['sex']);
                        family['underSponsorship'].options = res['underSponsorship'].map(item => ({
                            value: item.code === 'True',
                            label: item.description
                        }));
                        // employmentTabData['timescheme'].options = renameKeysInOptions(res['timeschemes']) 
                        additionalTabData['companySecondary'].options = renameKeysInOptions(res['company']);
                        userData['submitsTimesheets'] = userData['parking'];
                        additionalTabData['locationSecondary'].options = renameKeysInOptions(res['location']);
                        //employmentTabData['hcode'].options = renameKeysInOptions(res['seniority']);
                        personalTabData['homeCity'].options = renameKeysInOptions(res['town']);
                        personalTabData['homeCountry'].options = renameKeysInOptions(res['country']);
                        personalTabData['scndNationality'].options = renameKeysInOptions(res['country']);
                        personalTabData['nationality'].options = renameKeysInOptions(res['country']);
                        //#endregion


                        getLimitedData(selectedEmployee?.employeeID, "mnuEmployee").then((res) => {
                            let limitedData = res?.data[0];
                            setValue(`importantPersonalData.name`, limitedData?.name ?? "");
                            setValue(`importantPersonalData.employeeID`, limitedData?.employeeid ?? "");
                            setValue(`importantPersonalData.company`, limitedData?.company ?? "");
                            setValue(`importantPersonalData.branch`, limitedData?.branch ?? "");
                            setValue(`importantPersonalData.location`, limitedData?.location ?? "");
                            setValue(`importantPersonalData.function`, limitedData?.functionDesc ?? "");
                            setValue(`importantPersonalData.servLine`, limitedData?.serviceLine ?? "");
                            setValue(`importantPersonalData.department`, limitedData?.department ?? "");
                            setValue(`importantPersonalData.jobTitle`, limitedData?.jobtitle ?? "");
                            setValue(`importantPersonalData.seniority`, limitedData?.seniority ?? "");
                            setValue(`importantPersonalData.emplType`, limitedData?.etype ?? "");
                            getLimitedData(selectedEmployee?.employeeID, "SpecialDuties").then((res) => {
                                let specialDutiesData = (res?.data?.reduce((acc, obj) => {
                                    return acc.concat(Object.values(obj));
                                }, []));
                                setValue("specialDuties", specialDutiesData[0]);
                                setValue("servLine", limitedData?.serviceLine);
                                setValue("function", limitedData?.functionDesc);
                                //setValue("staffLevel", limitedData?.staffLevel);
                                setDropdowns(dropdownValues)
                                Object.keys(userData).forEach(key => {
                                    setValue(key, userData[key] ?? "");
                                    register(key);
                                });

                                getTimescheme(selectedEmployee?.employeeID).then((res) => {
                                    const timeschemes = res.data.reduce((acc, d) => {
                                        acc[d.month] = d.timescheme;
                                        return acc;
                                    }, {});
                                    const timescheme = res.data[0]?.timescheme;
                                    setOriginalUserData({ ...userData, timeschemes, timescheme });
                                    resetField('timescheme', { defaultValue: timescheme })
                                    resetField('timeschemes', { defaultValue: timeschemes })
                                    setFamilyDropdowns(family);
                                    setPersonalTabArray(Object.entries(personalTabData));
                                    setEmploymentTabArray(Object.entries(employmentTabData))
                                    setMiscellaneousTabArray(Object.entries(miscellaneousTabData))
                                    setAdditionalInfoArray(Object.entries(additionalTabData))
                                    setIsLoading(false);
                                })
                            })
                        })
                    }
                })
                // })
            })
        })
    }
    const descFields = ['company', 'branch', 'location', 'department', 'L4', 'L5', 'L6', 'seniority'];

    const getDropdowns = async (sParams) => {
        let dropdownValues = {};
        let family = { ...familyDropdowns };

        const res = await getDropdownValues(sParams);

        if (Object.keys(res).length > 0) {
            for (const key in res) {
                let result;
                let tab = undefined;
                let renamedField = renameKeysInOptions(res[key]);

                if (key in personalTabData) {
                    personalTabData[key].options = renamedField;
                    tab = personalTabData;
                } else if (key in employmentTabData) {
                    employmentTabData[key].options = renamedField;
                    tab = employmentTabData;
                } else if (key in miscellaneousTabData) {
                    miscellaneousTabData[key].options = renamedField;
                    tab = miscellaneousTabData;
                } else if (key in additionalTabData) {
                    additionalTabData[key].options = renamedField;
                    tab = additionalTabData;
                }

                if (key in family && !tab) {
                    family[key].options = renamedField;
                    tab = family;
                }

                dropdownValues[key] = renamedField;
            }
        }
        return dropdownValues;
    }
    const fetchMandatoryFields = async () => {
        try {
            const res = await getMandatoryFields("frmEmployee");
            let exceptions = ['dept', 'probationPeriodMonths', 'employementType', 'dob']
            const tabs = [personalTabData, employmentTabData, miscellaneousTabData, additionalTabData];
            const mandatoryFieldsMap = res.reduce((acc, field) => {
                acc[field.attribute] = field.FieldErrorMsg;
                return acc;
            }, {});
            tabs.forEach(tab => {
                for (const key in tab) {
                    if (exceptions.includes(key)) return;
                    if (tab.hasOwnProperty(key) && mandatoryFieldsMap[key]) {
                        tab[key].required = mandatoryFieldsMap[key];
                    }
                }
            });
            employmentTabData['department'].required = mandatoryFieldsMap['dept'];
            employmentTabData['department'].type = !isEdit ? 'dropdown' : 'button';
            employmentTabData['probPeriodinMonths'].required = mandatoryFieldsMap['probationPeriodMonths'];
            //employmentTabData['hcode'].required = mandatoryFieldsMap['hcode'];
            employmentTabData['emplType'].required = mandatoryFieldsMap['employementType'];
            personalTabData['dateOfBirth'].required = mandatoryFieldsMap['dob'];

        } catch (error) {
            console.error('Error fetching mandatory fields:', error);
        }
    };

    const fillExtraDropdowns = (extraDropdown, extraKey, dropdown, key) => {
        try {
            extraDropdown[extraKey].options = renameKeysInOptions(dropdown[key]);
        }
        catch {
            return null;
        }
    }

    function renameKeysInOptions(options) {
        return options.map(option => {
            const keys = Object.keys(option);
            const newObj = {
                value: option[keys[0]] !== null ? option[keys[0]] : option[keys[1]],
                label: option[keys[1]] !== null ? option[keys[1]] : option[keys[0]]
            };
            return newObj;
        });
    }

    const findObjectByValue = (array, valueToFind) => {
        if (!array || array.length <= 0) return;
        return (array.find(obj => obj.value === valueToFind))?.label;
    };

    const convertDate = (inputDate) => {
        if (!inputDate) return;
        const date = new Date(inputDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const isUniqueError = (data, isSucc = false) => {
        const tabs = [personalTabArray, employmentTabArray, miscellaneousTabArray, additionalInfoArray];
        // Create a map for each tab array for faster lookups
        const tabMaps = tabs.map(tab => {
            const map = new Map();
            tab.forEach(el => {
                // Check if el[1] has the key 'error' and remove it if present
                if (el[1].hasOwnProperty('error')) {
                    delete el[1].error;
                }
                map.set(el[0].toLowerCase(), el);
            });
            return map;
        });

        if (!isSucc) {
            data.forEach(obj => {
                tabMaps.forEach(tabMap => {
                    const key = obj['Field']?.toLowerCase();
                    if (key && tabMap.has(key)) {
                        const el = tabMap.get(key);
                        el[1].error = true;
                    }
                });
            });
        }

        setPersonalTabArray([...personalTabArray]);
        setEmploymentTabArray([...employmentTabArray])
        setMiscellaneousTabArray([...miscellaneousTabArray])
        setAdditionalInfoArray([...additionalInfoArray])
    }

    const onAddLeaveAnalysis = (employeeid, company, mappedData) => {
        Swal.fire({
            title: t('common:leaveAnalysis'),
            text: t('common:createLeaveAnalysisMess'),
            icon: "question",
            showCancelButton: true, // Enable the cancel button
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Yes</text>`,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">No</text>`,
            confirmButtonColor: '#A0A2FF',
        }).then((result) => {
            if (result.isConfirmed) {
                createLeaveAnalysis(employeeid, company).then((res) => {
                    const status = res?.status ?? 500;
                    if (status === 200) {
                        props.setEmployee(mappedData);
                        props.changeStep(mappedData);
                    }
                })
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                props.setEmployee(mappedData);
                props.changeStep(mappedData);
            }
        });
    }

    const {
        control,
        setValue,
        handleSubmit,
        formState: { errors },
        watch,
        getValues,
        register,
        reset, resetField,
    } = useForm({
        defaultValues: originalUserData
    });

    const { dirtyFields } = useFormState({ control })

    useEffect(() => {
        if (!originalUserData) return;
        reset(originalUserData);
    }, [originalUserData, reset]);

    const company = useWatch({ control, name: "company" })
    const branch = useWatch({ control, name: "branch" })
    const location = useWatch({ control, name: "location" })
    const buildingFloor = useWatch({ control, name: "buildingFloor" })
    const timescheme = useWatch({ control, name: "timescheme" })
    const timeschemes = useWatch({ control, name: "timeschemes" })

    useEffect(() => {
        // if(isEdit && !originalUserData) return
        if (!dirtyFields.company) return;
        setValue('branch', null)
        setValue('location', null)
        setValue('buildingFloor', null)
        setValue('timescheme', null)
        setValue('timeschemes', null)
    }, [company, dirtyFields.company]);

    useEffect(() => {
        // if(isEdit && !originalUserData) return
        if (!dirtyFields.branch) return;
        setValue('location', null)
        setValue('buildingFloor', null)
        setValue('timescheme', null)
        setValue('timeschemes', null)
    }, [branch, dirtyFields.branch]);

    useEffect(() => {
        // if(isEdit && !originalUserData) return
        if (!dirtyFields.location) return;
        setValue('timescheme', null)
        setValue('timeschemes', null)
        setValue('buildingFloor', null)
    }, [location, dirtyFields.location]);


    function onCancel() {
        // resetField("branch")
        // resetField("location")
        // resetField("buildingFloor")
        // resetField("timescheme")
        // resetField("timeschemes")
        setIsEditingMultitimeshemes(false)
    }

    function onSuccess() {
        resetField("branch", { defaultValue: getValues("branch") })
        resetField("location", { defaultValue: getValues("location") })
        resetField("buildingFloor", { defaultValue: getValues("buildingFloor") })
        resetField("timescheme", { defaultValue: getValues("timescheme") })
        resetField("timeschemes", { defaultValue: getValues("timeschemes") })
        setIsEditingMultitimeshemes(false)
    }

    function setTimeschemes(timeschemes) {
        setValue("timeschemes", timeschemes)
    }

    //#region Submit
    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            if (!isEdit) {
                let id = getValues('employeeID');
                let lastName = getValues('lastName') || "";
                let fullName = getValues('name') || "";
                let username = getValues('username') || "";
                let employeeNumber = getValues('employeeNumber') || "";
                let email = getValues('email') || "";
                let timeCard = getValues('timeCard') || "";
                try {
                    const res = await ValidateUniqueConstraints({ isEdit, id, lastName, fullName, username, employeeNumber, email, timeCard });
                    let data = res?.data;
                    if (data?.length > 0) {
                        isUniqueError(data);
                        showMessage({
                            message: t(`common:errNonUniqueValues`),
                            type: "danger",
                            duration: 3000,
                        })
                        setIsLoading(false);
                        return;
                    }
                } catch (error) {
                    setIsLoading(false);
                    console.error('Error validating unique constraints:', error);
                    return;
                }
            }

            delete data.salaryEligibility;
            const mappedData = Object.keys(data).reduce((acc, key) => {
                const description = data[key];
                let dropdownValue = dropdowns[key];
                let value;
                if (dropdownValue) {
                    dropdownValue.forEach(dropdown => {
                        if (dropdown.label === description) {
                            value = dropdown.value;
                        }
                    });
                }

                acc[key] = value || description;
                return acc;
            }, {});
            const formatDateToDMY = (date) => {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${day}-${month}-${year} ${hours}:${minutes}`;
            };
            const isValidDate = (dateString) => {
                // Regular expression to validate date format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)
                const regex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/;
                return regex.test(dateString);
            };
            for (const key in mappedData) {
                let field = mappedData[key];
                if (isValidDate(field)) {
                    const date = new Date(field);
                    mappedData[key] = formatDateToDMY(date);
                }
            }
            isUniqueError([], true);
            saveEmplSpecialDuties(selectedEmployee?.employeeID, selectedCompany?.code, data["specialDuties"]).then((res) => {
                // NOTE: What was this....?
                postUserData(isEdit, mappedData).then((res) => {
                    if (res?.status === 200) {
                        setSelectedEmployee(mappedData);
                        if (isEdit) {
                            showMessage({
                                message: t('common:allOperationsSuccMess'),
                                type: "success",
                                duration: 3000,
                            });
                        }
                        else {
                            onAddLeaveAnalysis(mappedData?.employeeID, mappedData?.company, mappedData);
                        }
                    } else {
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "error",
                            duration: 3000,
                        })
                    }
                    setIsLoading(false);
                })
            })
        }
        catch (error) {
            setIsLoading(false);
            console.log(error)
        }
    };

    const onErrorsFound = () => {
        showMessage({
            message: t('common:errReqFieldsNotFilled'),
            type: "danger",
            duration: 3000,
        })
    }
    //#endregion

    //#region Photograph
    const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

    const pickImage = async () => {
        const result = await ImagePicker.launchImageLibraryAsync({
            type: ["image/jpeg", "image/png"],
            allowsEditing: false,

            quality: 1,
        });
        // Web often provides fileSize in bytes
        if (!result.canceled) {
            const asset = result.assets[0]
            if (typeof asset.fileSize === "number" && asset.fileSize > MAX_BYTES) {
                alert(`Image too large. Please choose an image under ${MAX_BYTES} KB.`);
                return;
            }
            const uri = asset.uri.split(';base64,')[1];
            setValue('photograph', uri);
        }
    };
    //#endregion

    const handleEditPress = (key, value) => {
        setValue(key, value);
    }

    const getDescription = (key) => {
        return dropdowns[key];
    }

    //#region Watchers (watch for changes)
    const spouseWorkingForDeloitte = useWatch({ control, name: 'spouseWorkingForDeloitte' });
    const spouseName = useWatch({ control, name: 'spouseName' });
    const hasMedicalScheme = useWatch({ control, name: 'memberOfMedicalScheme' });
    const hasProvidentFund = useWatch({ control, name: 'memberOfProvFund' });
    const photograph = useWatch({ control, name: 'photograph' });
    //#endregion

    const importantPersonalData = isEdit ? ['employeeID', 'name', 'company', 'branch',
        'location', 'function', 'servLine', 'department', 'seniority', 'jobTitle',
        'emplType'
    ] : [];

    const showPopUp = (modal) => {
        let obj;
        switch (modal) {
            case 'family':
                obj = {
                    renderItems: renderFamilyDetails,
                    title: 'familyDetailsTitle',
                    allowsAddDelete: true,
                    key: 'ui'
                };
                break;
            case 'department':
                obj = {
                    renderItems: renderEmplDepts,
                    title: 'emplDeptsTitle',
                    allowsAddDelete: true,
                    key: 'rowguid'
                };
                break;
            // case 'multipleTimeschemes':
            //     obj = {
            //         renderItems: renderMultipleTimeschemes,
            //         title: 'multipleTimeschemes',
            //         allowsAddDelete: false,
            //         key: 'rowguid'
            //     };
            //     break;
            case 'salaryEligibility':
                obj = {
                    renderItems: renderSalaryEligibility,
                    title: 'salaryEligibilityTitle',
                    allowsAddDelete: false,
                    key: 'rowguid'
                };
                break;
            case 'reconcileBalances':
                obj = {
                    renderItems: renderReconciliation,
                    title: 'reconciliationBalancesTitle',
                    allowsAddDelete: false,
                    key: 'rowguid'
                };
                break;
            case 'schedule':
                obj = {
                    renderItems: renderSchedule,
                    title: 'scheduleTitle',
                    allowsAddDelete: false,
                    key: 'rowguid'
                };
                break;
            case 'provFund':
                obj = {
                    renderItems: renderProvidentFund,
                    title: 'scheduleTitle',
                    allowsAddDelete: false,
                    key: 'rowguid'
                };
                break;
            case 'medScheme':
                obj = {
                    renderItems: renderMedicalScheme,
                    title: 'scheduleTitle',
                    allowsAddDelete: false,
                    key: 'rowguid'
                };
                break;
            default:
                break;
        }
        setModalData(obj);
        toggleModal();
    };

    const handlePress = useCallback((setExpanded) => {
        setExpanded(prevState => !prevState);
    }, []);

    //#region Modal
    const toggleModal = () => {
        setModalVisible(!modalVisible);
    }
    //#endregion

    //#region Renderers
    const renderReconciliation = useCallback((modal, modalVisible, setModalVisible) => {
        return (
            <HRAdminReconciliationBalances props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                salaryEligibility: salaryEligibility,
                modal: modal,
                CalculateEmplRevisedAllowance: CalculateEmplRevisedAllowance,
                reconcileBalances: reconcileBalances,
                colors: colors,
                t: t,
                styles: styles,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
            }} />
        )

    }, [reconciliationBalances])

    const renderFields = useCallback((dataArr) => {
        return dataArr.map((k) => (
            <DisplayField
                key={k[0]}
                styles={styles}
                field={k}
                control={control}
                handleEditPress={handleEditPress}
                getValues={getValues}
                colors={colors}
                tempRange={tempRange}
                setTempRange={setTempRange}
                setValue={setValue}
                showPopUp={showPopUp}
                descFields={descFields}
                getDescription={getDescription}
                findObjectByValue={findObjectByValue}
                dropdowns={dropdowns}
                getLimitedData={getLimitedData}
                selectedEmployee={selectedEmployee}
                salaryEligibility={salaryEligibility}
                watch={watch}
                getDropdownValues={getDropdownValues}
                renameKeysInOptions={renameKeysInOptions}
                isEdit={isEdit}
                useEmployeeNoAsUsername={useEmployeeNoAsUsername}
                t={t}
                isEmployeeIDPascal={isEmployeeIDPascal}
                allowMultipleTimeschemes={allowMultipleTimeschemes}
                setIsEditingMultitimeshemes={setIsEditingMultitimeshemes}
                isEditingMultitimeshemes={isEditingMultitimeshemes}
                getEmployeeIdBasedOnLocation={getEmployeeIdBasedOnLocation}
            />
        ));
    }, [spouseName, spouseWorkingForDeloitte, salaryEligibility, hasMedicalScheme, hasProvidentFund, setValue, dropdowns]);

    const renderColumns = () => {
        const columns = [];
        for (let i = 0; i < importantPersonalData.length; i += 3) {
            columns.push(
                <View key={i} style={[styles.row, { width: '100%' }]}>
                    {importantPersonalData.slice(i, i + 3).map((value, index) => (
                        <View key={index} style={{ flex: 1, marginHorizontal: 4 }}>
                            <CustomLabel
                                value={t(`common:${value}`)}
                                type='EDIT_EMPLOYEE_IMPORTANT'
                                align='left'
                            />
                            <CustomControlledInput
                                name={`importantPersonalData.${value}`}
                                control={control}
                                editable={false}
                                type='EDIT_EMPLOYEE_IMPORTANT'
                            />
                        </View>
                    ))}
                </View>
            );
        }
        return columns;
    }

    function renderFamilyDetails(modal, modalVisible, setModalVisible) {
        return (
            <HRAdminFamilyDetails props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                modal: modal,
                colors: colors,
                t: t,
                styles: styles,
                common: common,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
                ActivityIndicator: ActivityIndicator,
                familyDropdowns: familyDropdowns,
                colors: colors
            }} />
        )
    }

    function renderProvidentFund(modal, modalVisible, setModalVisible) {
        return (
            <HRAdminProvidentFund props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                modal: modal,
                colors: colors,
                t: t,
                styles: styles,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
                ActivityIndicator: ActivityIndicator,
                getDropdownValues: getDropdownValues,
                renameKeysInOptions: renameKeysInOptions,
                selectedCompany: selectedCompany,
                colors: colors
            }} />
        )
    }

    function renderMedicalScheme(modal, modalVisible, setModalVisible) {
        return (
            <HRAdminMedicalScheme props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                modal: modal,
                colors: colors,
                t: t,
                styles: styles,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
                ActivityIndicator: ActivityIndicator,
                renameKeysInOptions: renameKeysInOptions,
                selectedCompany: selectedCompany,
                colors: colors
            }} />
        )
    }

    function renderSchedule(modal, modalVisible, setModalVisible) {
        return (
            <HRAdminSchedule props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                modal: modal,
                colors: colors,
                t: t,
                styles: styles,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
                ActivityIndicator: ActivityIndicator,
                colors: colors
            }} />
        )
    }
    //    const renderMultipleTimeschemes = (modal, modalVisible, setModalVisible) => {
    //     debugger
    //         return (
    //             <HRAdminEmployeeTimeSchemes props={{
    //                 selectedBranch: getValues('branch'),
    //                 selectedEmployee: selectedEmployee,
    //                 selectedLocation: getValues('location'),
    //                 selectedFloor: getValues('buildingFloor'),
    //             }} />
    //         )

    //     }
    const renderSalaryEligibility = (modal, modalVisible, setModalVisible) => {
        return (
            <HRAdminSalaryEligibility props={{
                selectedEmployee: selectedEmployee,
                convertDate: convertDate,
                isLoading: isLoading,
                modal: modal,
                colors: colors,
                t: t,
                styles: styles,
                modalVisible: modalVisible,
                setModalVisible: setModalVisible,
                ActivityIndicator: ActivityIndicator
            }} />
        )

    }

    const renderEmplDepts = (modal, modalVisible, setModalVisible) => {
        return (
            <HRAdminEmplDepts
                props={{
                    selectedEmployee: selectedEmployee,
                    convertDate: convertDate,
                    isLoading: isLoading,
                    modal: modal,
                    t: t,
                    styles: styles,
                    getDropdownValues: getDropdownValues,
                    dropdowns: dropdowns,
                    getValues: getValues,
                    watch: watch,
                    renameKeysInOptions: renameKeysInOptions,
                    employmentTabData: employmentTabData,
                    selectedCompany: selectedCompany,
                    modalVisible: modalVisible,
                    setModalVisible: setModalVisible,
                    emplCompany: emplCompany,
                    colors: colors,
                    onClose: onClose
                }}
            />
        );
    };

    const onClose = () => {
        setIsLoading(true)
        let options = employmentTabData['hcode'].options;
        getLimitedData(selectedEmployee?.employeeID, "Seniority").then((res) => {
            let limitedData = res?.data[0];
            let matchedOption = options.find(option => option.label === limitedData?.seniority);
            setValue('hcode', matchedOption.value)
            setValue(`importantPersonalData.seniority`, limitedData?.seniority);
        })
        setIsLoading(false)
    }

    function renderModal(modal) {
        return (
            <>
                {
                    modal &&
                    typeof modal.renderItems === 'function' &&
                    modal.renderItems(modal, modalVisible, setModalVisible)
                }
            </>
        );
    };
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion
    return (
        <>
            <SafeAreaView style={styles.mainView}>
                <ScrollView stickyHeaderIndices={[0]}>
                    {!isEditingMultitimeshemes &&
                        <View style={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            alignSelf: "flex-end",
                            marginRight: 15,
                        }}>
                            <CustomButton
                                text={t('common:save')}
                                onPress={handleSubmit(onSubmit, onErrorsFound)}
                                type="BUTTON_SAVE"
                                isGradient={true}
                            />
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={props.pressedOnSelectEmployee}
                                type="BUTTON_CANCEL"
                            />
                        </View>
                    }
                    <SafeAreaView style={[styles.subMain]}>
                        <SafeAreaView style={[common.cardstyle, { flexDirection: 'row', flex: 1, padding: 40 }]}>
                            <Pressable onPress={pickImage}>
                                <Image
                                    source={{ uri: 'data:image/jpeg;base64,' + (photograph || DEFAULTPROFILE) }}
                                    resizeMode='cover'
                                    style={styles.image}
                                />
                                <CustomLabel
                                    value={t(`common:upload`)}
                                    type='EDIT_EMPLOYEE_IMPORTANT'
                                />
                            </Pressable>
                            <View style={isEdit && styles.rowBox}>
                                {renderColumns()}
                            </View>
                        </SafeAreaView>
                        <SafeAreaView style={[common.cardstyle, { margin: 10 }]}>
                            <MemoizedCustomButton onPress={handlePress} t={t} text={t('navigate:personalInfo')} setExpanded={setIsPersonalExpanded} isExpanded={isPersonalExpanded} styles={styles} />
                            <CustomExpandableView expanded={isPersonalExpanded}>
                                <SafeAreaView style={common.divider} />
                                <Render
                                    render={renderFields}
                                    styles={styles}
                                    arr={personalTabArray}
                                />
                            </CustomExpandableView>
                        </SafeAreaView>
                        <SafeAreaView style={[common.cardstyle, { margin: 10, }]}>
                            <MemoizedCustomButton onPress={handlePress} t={t} text="Employment" setExpanded={setIsEmploymentExpanded} isExpanded={isEmploymentExpanded} styles={styles} />
                            <CustomExpandableView expanded={isEmploymentExpanded}>
                                <SafeAreaView style={common.divider} />
                                {isEditingMultitimeshemes ? <HRAdminEmployeeTimeSchemes

                                    onSuccess={onSuccess}
                                    onCancel={onCancel}
                                    branch={getValues("branch")}
                                    location={getValues("location")}
                                    floor={getValues("buildingFloor")}
                                    selectedTimeschemes={getValues("timeschemes")}
                                    setSelectedBranch={(branch) => setValue('branch', branch, { shouldDirty: false })}
                                    setSelectedLocation={(location) => setValue('location', location, { shouldDirty: false })}
                                    setSelectedFloor={(floor) => setValue('buildingFloor', floor, { shouldDirty: false })}
                                    setTimeschemes={setTimeschemes}
                                    employeeCompany={company}
                                    employeeID={selectedEmployee?.employeeID}

                                /> :
                                    <Render
                                        render={renderFields}
                                        styles={styles}
                                        arr={employmentTabArray}
                                    />
                                }
                            </CustomExpandableView>
                        </SafeAreaView>
                        <SafeAreaView style={[common.cardstyle, { margin: 10, }]}>
                            <MemoizedCustomButton onPress={handlePress} t={t} text="Miscellaneous" setExpanded={setIsMiscellaneousExpanded} isExpanded={isMiscellaneousExpanded} styles={styles} />
                            <CustomExpandableView expanded={isMiscellaneousExpanded}>
                                <SafeAreaView style={common.divider} />
                                <Render
                                    render={renderFields}
                                    styles={styles}
                                    arr={miscellaneousTabArray}
                                />
                            </CustomExpandableView>
                        </SafeAreaView>

                        {Object.values(additionalTabData).some(field => field.isVisible) &&
                            <SafeAreaView style={[common.cardstyle, { margin: 10, }]}>
                                <MemoizedCustomButton onPress={handlePress} t={t} text="additionalInfo" setExpanded={setIsAdditionalExpanded} isExpanded={isAdditionalExpanded} tyles={styles} />
                                <CustomExpandableView expanded={isAdditionalExpanded}>
                                    <SafeAreaView style={common.divider} />
                                    <Render
                                        render={renderFields}
                                        styles={styles}
                                        arr={additionalInfoArray}
                                    />
                                </CustomExpandableView>
                            </SafeAreaView>}
                    </SafeAreaView>
                </ScrollView >
            </SafeAreaView >

            <Modal
                isVisible={modalVisible}
                hasBackdrop={true}
                backdropColor={colors.background_approved}
                backdropOpacity={0.9}
                width={width}
                height={height}
                onBackdropPress={() => setModalVisible(false)}
                onRequestClose={() => { setModalVisible(false) }}
            >
                <View style={styles.centeredView}>
                    <View style={[styles.modalView, width < 1300 && styles.fullScreenModal]}>
                        {renderModal(modalData)}
                    </View>
                </View>
            </Modal>
        </>
    )
};

//#region Styles
const makeStyles = (colors, width, height, isPlatformWeb) => StyleSheet.create({
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    cell: {
        width: `${100 / 7}%`, // 7 columns
        paddingVertical: 2,
        alignItems: "center",
        alignContent: "center",
    },
    dayCell: {
        color: "#8A9E6A",
        fontFamily: "Roboto-Medium",
        fontSize: 14,
        fontWeight: '700',
        textAlign: "center",
        textTransform: 'uppercase',
    },
    timeCell: {
        fontSize: 18,
    },
    mainView: {
        flex: 1,
        height: '100%',
    },
    subMain: {
        flex: 1,
        marginHorizontal: 10,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
        paddingHorizontal: 5,
        justifyContent: 'space-around',
    },
    column: {
        width: '40%',
        height: '100%',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        width: '100%',
        margin: 15,
    },
    dropdownView: {
        width: '80%',
        flex: 1,
        marginBottom: 5,
    },
    cardView: {
        flex: 1,
        margin: '2%',
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: (isPlatformWeb ? colors.background_card : '#FFF'),
    },
    cardEmployees: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },
    selectedItem: {
        borderWidth: 4,
        borderColor: colors.buttons_primary,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },
    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 3,
    },
    employeePhotograph: {
        width: 30,
        height: 30,
        marginLeft: 10,
        borderRadius: 100,
        borderWidth: 1,
        verticalAlign: 'middle',
    },
    verticalLine: {
        backgroundColor: colors.background_gray,
        width: 3,
        height: '100%',
        marginHorizontal: '2%'
    },
    horizontalLine: {
        backgroundColor: colors.background_gray,
        width: '100%',
        height: 1.5,
        marginBottom: 3
    },
    container: {
        flex: 1,
    },
    box: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        marginTop: 5,
        flex: 1
    },
    image: {
        width: 150,
        height: 150,
        margin: 20,
        borderRadius: 50,
        borderWidth: 1,
    },
    infoContainer: {
        flexDirection: 'column',
    },
    rowBox: {
        flex: 1,
        alignItems: 'center',
        marginHorizontal: 20,
    },
    icons: {
        fontSize: 25,
        color: colors.text_primary,
        margin: '5%'
    },
    centeredView: {
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 1,
    },
    modalView: {
        margin: 20,
        backgroundColor: 'white',
        borderRadius: 20,
        padding: 35,
    },
    fullScreenModal: {
        width: '100%',
        height: '100%',
        borderRadius: 20,
        padding: 35,
        margin: 20,
    },
    button: {
        borderRadius: 20,
        padding: 10,
        elevation: 2,
    },
    buttonOpen: {
        backgroundColor: '#F194FF',
    },
    buttonClose: {
        backgroundColor: '#2196F3',
    },
    textStyle: {
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    modalText: {
        marginBottom: 15,
        textAlign: 'center',
    },
    cardStyleLocation: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    secondLine: {
        alignItems: 'center',
        margin: 5,
    },
    item: {
        flex: 1,
        justifyContent: 'center',
        marginHorizontal: '1%'
    },
    iconRow: {
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 10,
    },
    leftIcons: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rightIcon: {
        alignItems: 'flex-end',
    },
    icon: {
        fontSize: 30,
        color: 'black',
        marginHorizontal: 5,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
        flex: 1,
        flexDirection: 'row',
        width: '30%',
        alignSelf: 'flex-end',
    },
    leftColumn: {
        width: '25%'
    },
    middleColumn: {
        width: '60%'
    },
    rightColumn: {
        width: '3%',
    },
    label: {
        flex: 1,
        textAlign: 'left',
    },
    input: {
        flex: 1,
        marginVertical: '2%'
    },
    rowFlatList: {
        flexDirection: 'row',
    },
    nameColumn: {
        width: '40%',
    },
    itemFlatlist: {
        flex: 1,
        margin: 10
    }
});

//#endregion

export default HRAdminEditEmployeeScreen;